import { assoc, dissoc } from 'ramda';
import { getCompany, getSelectedCompanyId } from '@twnel/companies-login';
import { getTexts } from '@twnel/web-components';
import { isNotEmpty, toastDispacher } from 'utils';
import {
  SUCCESS_TYPE, ERROR_TYPE, BOT_DELETION,
  BOT_ERROR_GROUPS_GETTER, BOT_ERRORS_DETAILS_GETTER,
  BOT_CREATION, BOT_PUBLISHED, BOT_PUBLISHED_UPDATE,
} from 'utils/toastDispacher';
import schemaValidator from 'schemas/SchemaValidator';
import {
  UPDATE_BOTSPECS, UPDATE_PARSING_BOTSPEC, UPDATE_EDITOR_ERROR,
  UPDATE_SELECTED_BOTSPEC, UPDATE_SELECTED_VARIABLE_GROUP, UPDATE_LOADING_BOTSPECS,
  UPDATE_SELECTED_STORAGE_GROUP, UPDATE_SELECTED_FUNCTION_GROUP, RESET_SELECTIONS,
  UPDATE_BOT_ERROR_GROUPS, UPDATE_BOT_ERRORS_DETAILS, LOADING_BOT_ERRORS_DETAILS,
  LOADING_BOT_ERROR_GROUPS, RESET_CHATBOTS, RESTORE_BOTSPEC_PAST_STATE,
  CREATE_FOLDER, UPDATE_FOLDERS, UPDATE_CHATBOTS, UPDATE_CURRENT_BUSINESS_UNITS,
} from '../namespace';
import {
  getAllBotspecs, getBotspecInfo, getFolders, getTemplates,
} from '../selectors';

const NETWORK_ERROR = 'TwnelNetwork';

export const networkError = (error) => {
  if (!(error instanceof Error)) {
    return new Error(error);
  }
  return { ...error, name: NETWORK_ERROR };
};

export const resetChatbots = () => ({
  type: RESET_CHATBOTS,
});

export const updateSelectedBotspec = (id) => ({
  type: UPDATE_SELECTED_BOTSPEC,
  payload: { id },
});

export const updateSelectedVariableGroup = (groupId) => ({
  type: UPDATE_SELECTED_VARIABLE_GROUP,
  payload: { groupId },
});

export const updateSelectedFunctionGroup = (groupId) => ({
  type: UPDATE_SELECTED_FUNCTION_GROUP,
  payload: { groupId },
});

export const updateSelectedStorageGroup = (groupId) => ({
  type: UPDATE_SELECTED_STORAGE_GROUP,
  payload: { groupId },
});

export const resetSelections = () => ({
  type: RESET_SELECTIONS,
});

export const updateEditorError = (value) => ({
  type: UPDATE_EDITOR_ERROR,
  payload: { value },
});

export const updateParsingBotspec = (value) => ({
  type: UPDATE_PARSING_BOTSPEC,
  payload: { value },
});

export const throwNetworkError = (error) => {
  throw networkError(error);
};

export const updateBotspecs = (botspecs) => ({
  type: UPDATE_BOTSPECS,
  payload: { botspecs },
});

export const updateFolders = (folders) => ({
  type: UPDATE_FOLDERS,
  payload: { folders },
});

export const updateLoadingBotspecs = (value) => ({
  type: UPDATE_LOADING_BOTSPECS,
  payload: { value },
});

export const updateChatbots = (value) => ({
  type: UPDATE_CHATBOTS,
  payload: { value },
});

export const updateCurrentBUnits = (value) => ({
  type: UPDATE_CURRENT_BUSINESS_UNITS,
  payload: { value },
});

export const postBotspec = (botspec, actionNotifier) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  const bots = getAllBotspecs(getState());
  const oldChatbot = bots[botspec.id];
  const post = oldChatbot ? api.botspecs.update : api.botspecs.create;
  return post(botspec).then((updatedBotspec) => {
    if (actionNotifier) {
      toastDispacher({ ...actionNotifier, texts, type: SUCCESS_TYPE });
    }

    dispatch(updateBotspecs({ ...bots, [botspec.id]: { ...bots[botspec.id], ...updatedBotspec } }));
    if (actionNotifier === BOT_CREATION) {
      updateSelectedBotspec(botspec.id);
    }
  }).catch((error) => {
    toastDispacher({ ...actionNotifier, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  });
};

export const validateSpecJson = (json) => {
  const ajv = schemaValidator();
  const jsonObject = JSON.parse(json);
  const valid = ajv.validate(jsonObject);
  const errors = ajv.getErrorsAsString(ajv.validate.errors).split(',').reduce((accumulator, currentValue) => `${currentValue}\n${accumulator}`, '');

  return { valid, errors };
};

export const saveBotSpec = (botspec, actionNotifier) => (dispatch, getState) => {
  const { botspec: oldBotspec } = getBotspecInfo(getState());
  dispatch(postBotspec({ ...oldBotspec, ...botspec }, actionNotifier));
};

export const buildDefaultBot = () => ({
  entryPoint: 1,
  nodes: {
    1: {
      id: 1,
      data: {
        isEntryPoint: true,
        title: 'Send Message#1',
        properties: {
          title: 'Send Message#1',
          type: 'send_message',
          vars: {},
          messages: {},
          input: {},
          media: { mediaUrl: '' },
          closeChat: false,
          delay: 0,
          resultVariables: {},
        },
      },
      inputs: {
        rcv: {
          connections: [],
        },
      },
      outputs: {
        nxt: {
          connections: [],
        },
      },
      position: [248, 338],
      name: 'Send Message',
    },
  },
  data: {
    1: {
      properties: {
        title: 'Send Message#1',
        type: 'send_message',
        vars: {},
        messages: {},
        input: {},
        mediaUrl: '',
        metadata: { media: '' },
        closeChat: false,
        delay: 0,
        resultVariables: {},
      },
      connections: [],
      isEntryPoint: true,
    },
  },
});

export const createBotSpec = ({
  id,
  name,
  description,
  type = 'json',
  selectedFolder,
  specJson,
  actionNotifier,
}) => (dispatch, getState) => {
  const company = getCompany(getSelectedCompanyId(getState()))(getState());
  const chatbot = {
    id,
    name,
    description,
    company: company.id,
    country: company.country,
    rete_json: {
      id: `${id}@1.0.0`,
      name,
      entryPoint: 1,
      nodes: {},
      data: {},
      variables: {},
      ...type === 'visual' && buildDefaultBot(),
    },
    spec_json: specJson || {
      version: '1.0',
      variables: {
        user: {
          phone: { type: 'string', value: '' },
          country: { type: 'string', value: '' },
          name: { type: 'string', value: '' },
          tags: { type: 'array', value: [] },
          session_id: { type: 'string', value: '' },
        },
        chatbot: {},
        session: {},
      },
      files: {},
      functions: {},
      rules: {},
      messages: {},
      storage: {
        customer: {
          enable: false,
          drivers: {},
        },
        twnel: {
          enable: true,
          mode: 'secure',
        },
      },
      transitions: {},
      actions: {},
    },
    last_updated: Date.now(),
    enable: true,
    type,
    folder: selectedFolder,
  };

  return dispatch(postBotspec(chatbot, actionNotifier))
    .then(() => dispatch(updateSelectedBotspec(id)));
};

export const updateChatBot = (chatBotjson) => (dispatch, getState) => {
  const { selectedChatbot, chatbots, chatbot } = getBotspecInfo(getState());

  dispatch(
    updateBotspecs(assoc(selectedChatbot, { ...chatbot, rete_json: chatBotjson }, chatbots)),
  );
};

export const saveChatbot = (id, json) => (dispatch, getState) => {
  const { botspec } = getBotspecInfo(getState());
  if (botspec) {
    dispatch(postBotspec({ ...botspec, reteJson: { ...botspec.reteJson, ...json } }));
  }
};

export const deleteBotspec = (id) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const botspecs = getAllBotspecs(getState());
  const texts = getTexts(getState());
  const updatedBotspecs = dissoc(id, botspecs);
  return api.botspecs.delete(id).then(() => {
    toastDispacher({
      actionId: BOT_DELETION,
      texts,
      type: SUCCESS_TYPE,
    });
    dispatch(updateBotspecs(updatedBotspecs));
  }).catch(() => {
    toastDispacher({
      actionId: BOT_DELETION,
      texts,
      type: ERROR_TYPE,
    });
  });
};

// eslint-disable-next-line consistent-return
export const loadBotspecsIfNeeded = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const stateBotspecs = getAllBotspecs(getState());
  if (!isNotEmpty(stateBotspecs)) {
    return api.botspecs.getAll().then((botspecs) => {
      const reduceChatbots = botspecs.reduce((accum, chatbot) => (
        { ...accum, [chatbot.id]: { ...chatbot } }
      ), {});
      dispatch(updateLoadingBotspecs(false));
      dispatch(updateBotspecs(reduceChatbots));
    });
  }
};

// eslint-disable-next-line consistent-return
export const loadFoldersIfNeeded = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const folders = getFolders(getState());
  if (!isNotEmpty(folders)) {
    return api.chatbots.getFolders().then((data) => {
      const reduceChatbots = data.reduce((accum, folder) => (
        { ...accum, [folder.id]: { ...folder } }
      ), {});
      dispatch(updateFolders(reduceChatbots));
    });
  }
};

export const storeInContext = (key, value) => (dispatch, getState, getContext) => {
  const { updateContext } = getContext();
  updateContext({ [key]: value });
};

export const resetBotspecs = () => (dispatch) => {
  dispatch(updateLoadingBotspecs(true));
  dispatch(updateBotspecs({}));
  dispatch(loadBotspecsIfNeeded());
};

export const resetFolders = () => (dispatch) => {
  dispatch(updateFolders({}));
  dispatch(loadFoldersIfNeeded());
};

export const makeApiCall = (apiRequest) => {
  const {
    url,
    method,
    params,
    headers,
    body,
  } = apiRequest;

  let queryParams = '';
  let config = null;
  if (method === 'get') {
    queryParams = `?${Object.keys(params).filter((key) => params[key] !== null).map((key) => (
      `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )).join('&')}`;

    config = {
      method,
      headers,
      mode: 'cors',
    };
  } else if (method === 'delete') {
    config = {
      method,
      headers,
      mode: 'cors',
    };
  } else {
    config = {
      method,
      headers,
      body,
      mode: 'cors',
    };
  }

  const finalUrl = `${url}${queryParams}`;

  return fetch(finalUrl, config);
};

export const loadingBotErrorGroups = (id, loading) => ({
  type: LOADING_BOT_ERROR_GROUPS,
  payload: { id, loading },
});

export const updateBotErrorGroups = (id, errorsGroups) => ({
  type: UPDATE_BOT_ERROR_GROUPS,
  payload: { id, errorsGroups },
});

export const loadingBotErrorsDetails = (id, loading) => ({
  type: LOADING_BOT_ERRORS_DETAILS,
  payload: { id, loading },
});

export const updateBotErrorsDetails = (id, gid, errorsDetails) => ({
  type: UPDATE_BOT_ERRORS_DETAILS,
  payload: { id, gid, errorsDetails },
});

export const getErrorGroups = ({ id, start, end }) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const reduxState = getState();
  const texts = getTexts(reduxState);
  dispatch(loadingBotErrorGroups(id, true));
  return api.chatbots.getErrorGroups({ id, start, end }).then((errorsGroups) => {
    const errorsInfo = errorsGroups;
    delete errorsInfo.query;
    dispatch(updateBotErrorGroups(id, errorsInfo));
  }).catch((error) => {
    dispatch(loadingBotErrorGroups(id, false));
    toastDispacher({ actionId: BOT_ERROR_GROUPS_GETTER, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  });
};

export const getErrorDetails = ({
  id, start, end, gid,
}) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const reduxState = getState();
  const texts = getTexts(reduxState);
  dispatch(loadingBotErrorsDetails(id, true));
  return api.chatbots.getErrorDetails({
    id, start, end, gid,
  }).then((errorsDetails) => {
    const errorsInfo = errorsDetails;
    delete errorsInfo.query;
    dispatch(updateBotErrorsDetails(id, gid, errorsInfo));
  }).catch((error) => {
    dispatch(loadingBotErrorsDetails(id, false));
    toastDispacher({ actionId: BOT_ERRORS_DETAILS_GETTER, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  });
};

export const restoreBotspecPastState = () => (dispatch, getState) => {
  const { botspec } = getBotspecInfo(getState());
  if (botspec) {
    dispatch({
      type: RESTORE_BOTSPEC_PAST_STATE,
      payload: { id: botspec.id },
    });
  }
};

export const duplicateBotspec = (botspecId, name, description, duplicateId) => async (
  dispatch, getState,
) => {
  const botspecs = getAllBotspecs(getState());
  const botspec = botspecs[botspecId];

  if (botspec.rete_json.template) {
    delete botspec.rete_json.template;
  }

  const duplicatedBotspec = {
    ...botspec,
    id: duplicateId,
    name,
    description,
    rete_json: {},
    spec_json: {},
  };

  const updateBotSpec = {
    ...duplicatedBotspec,
    rete_json: { ...botspec.rete_json, id: `${duplicateId}@1.0.0`, name },
    spec_json: botspec.spec_json,
  };
  try {
    await dispatch(postBotspec(duplicatedBotspec, {
      actionId: BOT_CREATION,
      elementName: name,
    }));
    await dispatch(postBotspec(updateBotSpec, {
      actionId: BOT_CREATION,
      elementName: name,
    }));
    dispatch(updateSelectedBotspec(duplicateId));
  } catch (error) {
    throwNetworkError(error);
  }
  return botspec.type;
};

export const createTemplate = (templateId, name, id) => async (
  dispatch, getState, getContext,
) => {
  const company = getCompany(getSelectedCompanyId(getState()))(getState());
  const templates = getTemplates(getState());
  let template = templates.find((t) => t.id === templateId);
  const bots = getAllBotspecs(getState());
  const { api } = getContext();
  const { update, create } = api.botspecs;
  const texts = getTexts(getState());
  template = dissoc('_id', template);

  const duplicatedBotspec = {
    ...template,
    id,
    name,
    description: '',
    company: company.id,
    country: company.country,
    folder: '',
    rete_json: {},
    spec_json: {},
  };

  const updateBotspec = {
    ...duplicatedBotspec,
    rete_json: { ...template.rete_json, id: `${id}@1.0.0`, name },
    spec_json: template.spec_json,
  };

  try {
    await create(duplicatedBotspec);
    await update(updateBotspec);
    dispatch(
      updateBotspecs({
        ...bots,
        [updateBotspec.id]: { ...bots[updateBotspec.id], ...updateBotspec },
      }),
    );
    dispatch(updateSelectedBotspec(id));
  } catch (error) {
    toastDispacher({ actionId: BOT_CREATION, texts, type: ERROR_TYPE });
    throwNetworkError(error);
  }
  return template.type;
};

export const saveBotspecFolderType = (botspecId, folderId) => (dispatch, getState) => {
  const botspecs = getAllBotspecs(getState());
  const botspec = botspecs[botspecId];

  return dispatch(postBotspec({ ...botspec, folder: folderId }));
};

export const createBotspecFolder = (name, parentId) => (dispatch, _, getContext) => {
  const { api } = getContext();

  return api.chatbots.createFolder({ name, parent: parentId })
    .then((folder) => {
      dispatch({
        type: CREATE_FOLDER,
        payload: { folder },
      });
    });
};

export const deleteBotspecFolder = (folderId) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const botspecs = getAllBotspecs(getState());
  const folders = getFolders(getState());

  return api.chatbots.deleteFolder({ id: folderId })
    .then(() => {
      const deletedFolder = folders[folderId];
      const classifiedBotspecs = Object.keys(botspecs).reduce((accum, key) => {
        const botspec = botspecs[key];
        if (botspec.folder === deletedFolder.id) {
          return {
            ...accum,
            [key]: { ...botspec, folder: deletedFolder.parent.length > 0 ? deletedFolder.parent : '' },
          };
        }
        return accum;
      }, botspecs);

      const { parent } = deletedFolder;
      const childs = Object.values(folders).filter((folder) => folder.parent === deletedFolder.id);

      const modifiedFolders = childs.reduce((accum, folder) => ({
        ...accum,
        [folder.id]: { ...folder, parent },
      }), folders);

      delete modifiedFolders[folderId];

      dispatch(updateBotspecs(classifiedBotspecs));
      dispatch(updateFolders(modifiedFolders));
    });
};

export const updateFolder = (folderId, parentId, name) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const folders = getFolders(getState());

  return api.chatbots.updateFolder(
    { id: folderId, ...name && { name }, parent: parentId },
  )
    .then(() => {
      dispatch(updateFolders(
        {
          ...folders,
          [folderId]: {
            ...folders[folderId],
            parent: parentId,
            ...name && { name },
          },
        },
      ));
    });
};

export const getChatbots = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  return api.chatbots.getAll()
    .then((chatbots) => dispatch(updateChatbots(chatbots)));
};

export const getCurrentBusinessUnits = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  return api.chatbots.getChatbotBUnits()
    .then((businessUnits) => dispatch(updateCurrentBUnits(businessUnits)));
};

export const createChatbotBUnit = (chatbotId, businessUnitId) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  return api.chatbots.createChatbotBUnit({ chatbotId, businessUnitId });
};

export const createChatbot = (
  {
    name, answer, botspecId, country, description,
    enable, type, businessUnitsInfo,
  },
) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  return api.chatbots.create(
    {
      name, botspec_id: botspecId, country, description, enable, type, answer,
    },
  )
    .then((newChatbot) => {
      const businessUnitPromises = [];
      businessUnitsInfo.forEach((businessUnit) => {
        businessUnitPromises.push(dispatch(createChatbotBUnit(newChatbot.id, businessUnit.id)));
      });
      return Promise.all(businessUnitPromises);
    })
    .then(() => {
      toastDispacher({
        actionId: BOT_PUBLISHED,
        texts,
        type: SUCCESS_TYPE,
      });
    })
    .catch((error) => {
      toastDispacher({ actionId: BOT_PUBLISHED, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

export const deleteChatbotBUnit = (chatbotId, businessUnitId) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  return api.chatbots.deleteChatbotBUnit({ chatbotId, businessUnitId });
};

export const updateChatbotPublished = (
  {
    id, name, answer, botspecId, description,
    enable, type, businessUnitsInfo,
    businessUnitsUpdate,
  },
) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  return api.chatbots.update({
    id, name, answer, description, type, enable, botspec_id: botspecId,
  })
    .then(() => {
      const businessUnitPromises = [];
      businessUnitsUpdate.forEach((businessUnit) => {
        businessUnitPromises.push(dispatch(deleteChatbotBUnit(id, businessUnit.id)));
      });
      return Promise.all(businessUnitPromises);
    })
    .then(() => {
      const businessUnitPromises = [];
      businessUnitsInfo.forEach((businessUnit) => {
        businessUnitPromises.push(dispatch(createChatbotBUnit(id, businessUnit.id)));
      });
      return Promise.all(businessUnitPromises);
    })
    .then(() => {
      toastDispacher({
        actionId: BOT_PUBLISHED_UPDATE,
        texts,
        type: SUCCESS_TYPE,
      });
    })
    .catch((error) => {
      toastDispacher({ actionId: BOT_PUBLISHED_UPDATE, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};
